import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Badge } from "../../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { EnvelopeSimple, PaperPlaneTilt, CheckCircle, WarningCircle, Trash } from "@phosphor-icons/react";

const STATUS_STYLE = {
  verified: "bg-emerald-50 text-emerald-700 border-emerald-300",
  failed: "bg-rose-50 text-rose-700 border-rose-300",
  not_configured: "bg-zinc-100 text-zinc-700 border-zinc-300",
};

const EMPTY = { from_name: "", from_email: "", reply_to: "", host: "", port: 587, security: "starttls", username: "", password: "" };

export default function EmailSenderPanel({ params }) {
  const [s, setS] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/admin/email-settings", { params }).then(({ data }) => {
    setS(data);
    setForm({ ...EMPTY, from_name: data.from_name || "", from_email: data.from_email || "", reply_to: data.reply_to || "", host: data.host || "", port: data.port || 587, security: data.security || "starttls", username: data.username || "" });
  }).catch((e) => toast.error(formatApiError(e)));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [params.tenant_id]);

  const applyPreset = (k) => { const p = s.presets[k]; if (p) setForm({ ...form, ...p }); };
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/email-settings", { ...form, port: Number(form.port), password: form.password || null, reply_to: form.reply_to || null, username: form.username || null, enabled: true, ...params });
      setS(data); setForm((f) => ({ ...f, password: "" }));
      if (data.status === "verified") toast.success("Connected — emails will now go from your mail server");
      else toast.error(data.last_error || "Connection failed");
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy(false);
  };
  const test = async () => {
    setBusy(true);
    try { const { data } = await api.post("/admin/email-settings/test", null, { params }); toast.success(`Test email sent from ${data.sent_from}`); }
    catch (e) { toast.error(formatApiError(e)); }
    setBusy(false);
  };
  const remove = async () => {
    setBusy(true);
    try { const { data } = await api.delete("/admin/email-settings", { params }); setS(data); setForm(EMPTY); toast.success("SMTP settings removed — using Emergent-managed sender"); }
    catch (e) { toast.error(formatApiError(e)); }
    setBusy(false);
  };

  if (!s) return <div className="text-sm text-zinc-500">Loading…</div>;
  const verified = s.status === "verified";

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4" data-testid="email-sender-panel">
      <div className="lg:col-span-2 border border-zinc-200 bg-white rounded-sm p-6 space-y-5">
        <div className="flex items-center justify-between">
          <div>
            <div className="font-display text-xl font-bold flex items-center gap-2"><EnvelopeSimple size={20} /> Email configuration</div>
            <div className="text-xs text-zinc-500 mt-1">Welcome, activation and password-reset emails will be sent through your company's mail server.</div>
          </div>
          <Badge variant="outline" className={`rounded-sm ${STATUS_STYLE[s.status] || STATUS_STYLE.not_configured}`} data-testid="email-domain-status">{s.status?.replace("_", " ")}</Badge>
        </div>
        {!verified && (
          <div className="flex items-start gap-2 text-xs bg-amber-50 border border-amber-200 text-amber-800 rounded-sm p-3" data-testid="email-provider-missing">
            <WarningCircle size={16} className="shrink-0 mt-0.5" />
            <span>{s.status === "failed" ? s.last_error : "Not connected yet — emails currently go out via the Emergent-managed sender."}</span>
          </div>
        )}

        <div>
          <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold mb-2">Sender</div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div><Label className="text-xs">Sender name</Label><Input value={form.from_name} onChange={set("from_name")} placeholder="Acme Corp HR" className="rounded-sm mt-1" data-testid="email-from-name" /></div>
            <div><Label className="text-xs">Email ID (from address)</Label><Input value={form.from_email} onChange={set("from_email")} placeholder="hr@yourcompany.com" className="rounded-sm mt-1 font-mono-plex" data-testid="email-from-address" /></div>
            <div className="md:col-span-2"><Label className="text-xs">Reply-to (optional)</Label><Input value={form.reply_to} onChange={set("reply_to")} placeholder="people@yourcompany.com" className="rounded-sm mt-1 font-mono-plex" data-testid="email-reply-to" /></div>
          </div>
        </div>

        <div>
          <div className="flex items-center justify-between mb-2">
            <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold">SMTP server</div>
            <div className="flex gap-1">
              {Object.keys(s.presets || {}).map((k) => (
                <button key={k} onClick={() => applyPreset(k)} className="text-[11px] px-2 py-0.5 border border-zinc-200 rounded-sm hover:bg-zinc-50 capitalize" data-testid={`smtp-preset-${k}`}>{k}</button>
              ))}
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-6 gap-3">
            <div className="md:col-span-3"><Label className="text-xs">SMTP host</Label><Input value={form.host} onChange={set("host")} placeholder="smtp.yourcompany.com" className="rounded-sm mt-1 font-mono-plex" data-testid="smtp-host" /></div>
            <div><Label className="text-xs">Port</Label><Input type="number" value={form.port} onChange={set("port")} className="rounded-sm mt-1 font-mono-plex" data-testid="smtp-port" /></div>
            <div className="md:col-span-2"><Label className="text-xs">Security</Label>
              <Select value={form.security} onValueChange={(v) => setForm({ ...form, security: v })}>
                <SelectTrigger className="rounded-sm mt-1" data-testid="smtp-security"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="starttls">STARTTLS (port 587)</SelectItem>
                  <SelectItem value="ssl">SSL / TLS (port 465)</SelectItem>
                  <SelectItem value="none">None (port 25)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="md:col-span-3"><Label className="text-xs">Mailbox username (full email) *</Label><Input value={form.username} onChange={set("username")} placeholder="hr@yourcompany.com" className="rounded-sm mt-1 font-mono-plex" data-testid="smtp-username" autoComplete="off" /></div>
            <div className="md:col-span-3"><Label className="text-xs flex items-center gap-2">Password / App password * {s.password_set && <Badge variant="outline" className="rounded-sm bg-emerald-50 text-emerald-700 border-emerald-300" data-testid="smtp-password-set">saved</Badge>}</Label>
              <Input type="password" value={form.password} onChange={set("password")} placeholder={s.password_set ? "Leave blank to keep saved password" : "App password / SMTP password"} className="rounded-sm mt-1 font-mono-plex" data-testid="smtp-password" autoComplete="new-password" />
            </div>
          </div>
          <div className="text-[11px] text-zinc-500 mt-2"><b>Office 365 / Outlook:</b> username is the mailbox email; "Authenticated SMTP" must be enabled for that mailbox (Microsoft 365 admin → Users → Mail → Manage email apps) or use an App Password. <b>Gmail / Google Workspace:</b> enable 2-step verification and use an <b>App Password</b>. Saving sends a test mail to you from the configured address — status turns <b>verified</b> only if that succeeds.</div>
        </div>

        <div className="flex flex-wrap gap-2 justify-end">
          {s.status !== "not_configured" && <Button variant="outline" onClick={remove} disabled={busy} className="rounded-sm text-rose-600" data-testid="email-remove-settings"><Trash size={14} className="mr-1" /> Remove</Button>}
          <Button variant="outline" onClick={test} disabled={busy} className="rounded-sm" data-testid="email-send-test"><PaperPlaneTilt size={14} className="mr-1" /> Send me a test</Button>
          <Button onClick={save} disabled={busy || !form.host || !form.from_email || !form.username || (!form.password && !s.password_set)} className="btn-zenith rounded-sm" data-testid="email-save-sender">{busy ? "Connecting…" : "Save & connect"}</Button>
        </div>
      </div>

      <div className="border border-zinc-200 bg-gradient-to-br from-adb-navy/5 to-adb-green/5 rounded-sm p-5 text-sm space-y-3">
        <div className="text-[10px] uppercase tracking-widest text-adb-navy font-bold">How it works</div>
        <ol className="list-decimal pl-4 space-y-2 text-zinc-700 text-xs leading-relaxed">
          <li>Enter the email ID employees should see as the sender.</li>
          <li>Fill in your mail server details (or pick a preset such as Gmail / Outlook / Zoho).</li>
          <li>Click <b>Save &amp; connect</b> — we log in to the server to confirm the credentials work.</li>
          <li>Once <b>verified</b>, every email leaves from your mail server. Until then we fall back to the Emergent-managed sender.</li>
        </ol>
        {verified && <div className="flex items-center gap-2 text-emerald-700 text-xs font-bold" data-testid="email-active-sender"><CheckCircle size={16} weight="fill" /> Active: {s.from_name} &lt;{s.from_email}&gt; via {s.host}:{s.port}</div>}
      </div>
    </div>
  );
}
